import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectCartItems, selectCartTotals, clearCart } from "../../context/MyCartSlice";
import http from "../../api/http"; // ton axios préconfiguré
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { FiTruck, FiHome, FiChevronRight } from "react-icons/fi";
import "./checkout.css";
import { useAuth } from "../../context/AuthContext";
import { getStoredAuth } from "../../context/auth-storage";
import { useNavigate } from "react-router-dom";
const fmt = (x) =>
  Number(x ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CheckoutPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const items = useSelector(selectCartItems);
  const totals = useSelector(selectCartTotals);
  const { user } = useAuth();
  const codTiers = user?.codtiers ?? getStoredAuth()?.codtiers ?? null;

  // UI state
  const [mode, setMode] = useState(null); // 'COMPTOIR' | 'LIVRAISON' | null
  const [loading, setLoading] = useState(false);

  // Livraison (profil)
  const [profile, setProfile] = useState({ address: "", phone: "" });
  const [dirty, setDirty] = useState(false);

  const guid = useMemo(() => uuidv4(), []); // guid de la commande

  const groups = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const db = it.dbName || "Amen";
      if (!map.has(db)) map.set(db, []);
      map.get(db).push({ CodArt: it.CodArt, Qte: Number(it.qty || 1) });
    }
    return Array.from(map.entries()).map(([dbName, product]) => ({ dbName, product }));
  }, [items]);

  const isCartEmpty = items.length === 0;
  useEffect(() => {
    if (codTiers) {
      http.defaults.headers.common["x-codtiers"] = codTiers;
    } else {
      delete http.defaults.headers.common["x-codtiers"];
    }
  }, [codTiers]);
  // ===================== Comptoir =====================
  const submitComptoir = async () => {
    if (isCartEmpty) return toast.error("Votre panier est vide.");
    if (!codTiers) return toast.error("Client non identifié (CodTiers manquant).");
    if (!Array.isArray(groups) || groups.length === 0) {
      return toast.error("Aucune ligne à commander.");
    }

    setLoading(true);

    try {
      for (const g of groups) {
        if (!g?.dbName) throw new Error("Group dbName manquant");
        if (!Array.isArray(g.product) || g.product.length === 0) throw new Error("Group product vide");

        const payload = {
          Guid: uuidv4(),       // un GUID par BC
          CodTiers: codTiers,
          product: g.product,   // seulement les lignes de ce dépôt
          dbName: g.dbName,     // Amen / Amen_C
          livraison: false,
        };

        await http.post("/api/bc", payload);
      }

      toast.success("Vos commandes comptoir ont été créées (par dépôt) ✅");
      dispatch(clearCart());
      navigate("/espace", { replace: true });
    } catch (e) {
      console.error("submitComptoir error", {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      });
      toast.error(e?.response?.data?.msg || e?.response?.data?.message || "Échec création de commande.");
    } finally {
      setLoading(false);
    }
  };




  // ===================== Livraison =====================
  // 1) Pré-remplir adresse/tel (à l’entrée en mode Livraison)
  useEffect(() => {
    if (mode !== "LIVRAISON") return;
    (async () => {
      try {
        setLoading(true);
        // adapte les endpoints profil si besoin
        const { data } = await http.get("/api/profile/me");
        setProfile({
          address: data?.address || "",
          phone: data?.phone || "",
        });
        setDirty(false);
      } catch {
        // profil vierge => rester vide
      } finally {
        setLoading(false);
      }
    })();
  }, [mode]);

  const canSubmitLivraison = profile.address.trim() && profile.phone.trim();

  const submitLivraison = async () => {
    if (isCartEmpty) return toast.error("Votre panier est vide.");
    if (!codTiers) return toast.error("Client non identifié (CodTiers manquant).");
    if (!canSubmitLivraison) return toast.error("Adresse et téléphone sont obligatoires.");
    if (!Array.isArray(groups) || groups.length === 0) {
      return toast.error("Aucune ligne à commander.");
    }

    setLoading(true);

    try {
      // 1) update profil si modifié
      if (dirty) {
        await http.put("/api/profile/me", {
          address: profile.address.trim(),
          phone: profile.phone.trim(),
        });
      }

      // 2) créer UNE commande par base (Amen / Amen_C)
      for (const g of groups) {
        if (!g?.dbName) throw new Error("Group dbName manquant");
        if (!Array.isArray(g.product) || g.product.length === 0) throw new Error("Group product vide");

        const payload = {
          Guid: uuidv4(), // GUID différent par BC
          CodTiers: codTiers,
          product: g.product,
          dbName: g.dbName,
          livraison: true,
        };

        try {
          await http.post("/api/bc", payload);
        } catch (e) {
          console.error("submitLivraison: échec sur le dépôt", g.dbName, {
            status: e?.response?.status,
            data: e?.response?.data,
            msg: e?.message,
          });
          throw e; // stoppe ici => pas de clearCart
        }
      }

      toast.success("Commandes livraison créées avec succès ✅");
      dispatch(clearCart());
      navigate("/espace", { replace: true });
    } catch (e) {
      console.error("submitLivraison error", {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      });
      toast.error(e?.response?.data?.msg || e?.response?.data?.message || "Échec création de commande.");
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="checkout-wrap">
      <aside className="co-summary">
        <h2>Récapitulatif de la commande</h2>

        {items.map((it) => (
          <div className="co-line" key={it.CodArt}>
            <div className="co-thumb">
              {it.image ? <img src={it.image} alt="" /> : <div className="ph" />}
            </div>
            <div className="co-info">
              <div className="co-title">{it.LibArt}</div>
              <div className="co-sub">
                {it.qty} × <b>{fmt(it.PrixTTC)} DT</b>
              </div>
            </div>
          </div>
        ))}

        <div className="co-totalRow">
          <span>NET HT</span>
          <b>{fmt(totals.totalHT)} DT</b>
        </div>

        <div className="co-totalRow">
          <span>Livraison</span>
          <b>Gratuite</b>
        </div>

        <div className="co-totalRow">
          <span>Remise</span>
          <b>{fmt(totals.remise)} DT</b>
        </div>

        <div className="co-totalRow">
          <span>TVA</span>
          <b>{fmt(totals.totalTVA)} DT</b>
        </div>

        <hr />

        <div className="co-grand">
          <span>Total</span>
          <b>{fmt(totals.total)} DT</b>
        </div>

        <button
          className="co-btn co-btn--primary"
          disabled={isCartEmpty || loading}
          onClick={() => setMode("LIVRAISON")}
        >
          <FiTruck />
          <span>Livraison</span>
          <FiChevronRight />
        </button>

        <button
          className="co-btn"
          disabled={isCartEmpty || loading}
          onClick={() => setMode("COMPTOIR")}
        >
          <FiHome />
          <span>Comptoir</span>
          <FiChevronRight />
        </button>
      </aside>


      <section className="co-panel">
        {!mode && <div className="co-placeholder">Choisissez un mode : <b>Livraison</b> ou <b>Comptoir</b>.</div>}

        {mode === "COMPTOIR" && (
          <div className="co-box">
            <h3>Retrait au comptoir</h3>
            <p>Votre commande sera préparée pour un retrait au magasin.</p>
            <button className="co-btn co-btn--primary" disabled={loading} onClick={submitComptoir}>
              Confirmer ma commande
            </button>
          </div>
        )}

        {mode === "LIVRAISON" && (
          <div className="co-box">
            <h3>Adresse de livraison</h3>

            <label className="co-label">Adresse complète *</label>
            <textarea
              className="co-input"
              rows={3}
              value={profile.address}
              onChange={(e) => { setProfile(p => ({ ...p, address: e.target.value })); setDirty(true); }}
              required
            />

            <label className="co-label">Téléphone *</label>
            <input
              className="co-input"
              value={profile.phone}
              onChange={(e) => { setProfile(p => ({ ...p, phone: e.target.value })); setDirty(true); }}
              required
            />

            <div className="co-actions">
              <button
                className="co-btn co-btn--primary"
                disabled={loading || !canSubmitLivraison}
                onClick={submitLivraison}
              >
                Valider et créer la commande
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
